import { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { stagger, fadeUp } from '@/lib/animations';
import { Factory, Ruler } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader } from '@/components/ui/card';
import { toast } from 'sonner';
import { PageHeader, LoadingState, DataTable, type Column } from '@/components/common';
import { DetailLayout, DetailList, RailCard, StatStrip } from '@/components/common/DetailPanels';
import {
  yardApi,
  type YardPipeTally,
  type YardPipeJoint,
  type PipeCondition,
} from '@/api/services/yard.api';
import { getGradeLabel, getPipeTypeLabel, pipeConditionBadge } from '@/lib/pipe';
import { formatDate, formatNumber } from '@/lib/utils';
import { getErrorMessage } from '@/api/client';

/**
 * One pipe tally — the string it describes, and every joint counted into it.
 *
 * A tally is a document: it carries the heat number and mill that a mill
 * certificate is matched against, and it gets quoted when pipe is called out to
 * a well. The joint list is also long enough that a sheet was the wrong shape
 * for it — the old one scrolled inside a 48-tall box.
 */
export default function YardPipeTallyDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [tally, setTally] = useState<YardPipeTally | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    yardApi.pipeTally
      .getById(id)
      .then((data) => !cancelled && setTally(data))
      .catch((error) => {
        if (cancelled) return;
        toast.error(getErrorMessage(error));
        navigate('/yard/pipe-tally');
      })
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [id, navigate]);

  if (loading) return <LoadingState variant="detail" />;
  if (!tally) return null;

  const joints = tally.jointsDetail ?? [];
  const condition = pipeConditionBadge(tally.condition);

  // Computed server-side from the joints. Falls back to the stored totals for
  // any response that predates the figures being returned.
  const f = tally.figures;
  const totalFt = f?.totalFootage ?? tally.totalFootage;
  const usableFt = f?.usableFootage ?? tally.totalFootage;
  const shortfall = Math.round((totalFt - usableFt) * 100) / 100;

  const jointColumns: Column<YardPipeJoint>[] = [
    {
      key: 'jointNumber',
      header: 'Joint',
      sortable: true,
      className: 'w-[80px]',
      render: (j) => <span className="font-medium tabular-nums">#{j.jointNumber}</span>,
    },
    {
      key: 'length',
      header: 'Length',
      sortable: true,
      className: 'text-right',
      render: (j) => <span className="block text-right tabular-nums">{j.length} ft</span>,
    },
    {
      key: 'serialNumber',
      header: 'Serial',
      render: (j) => (
        <span className="text-muted-foreground">
          {j.serialNumber || <span className="text-muted-foreground/60">—</span>}
        </span>
      ),
    },
    {
      key: 'condition',
      header: 'Condition',
      sortable: true,
      render: (j) => {
        const c = pipeConditionBadge(j.condition as PipeCondition);
        return <Badge variant={c.variant}>{c.label}</Badge>;
      },
    },
    {
      key: 'inspectionDate',
      header: 'Inspected',
      sortable: true,
      render: (j) => (
        <span className="text-muted-foreground">
          {j.inspectionDate ? formatDate(j.inspectionDate) : '—'}
        </span>
      ),
    },
  ];

  return (
    <motion.main className="space-y-5" variants={stagger} initial="hidden" animate="show">
      <motion.div variants={fadeUp}>
        <PageHeader
          breadcrumbs={[
            { label: 'Home', href: '/' },
            { label: 'Pipe Tally', href: '/yard/pipe-tally' },
            { label: tally.tallyNumber },
          ]}
          title={tally.tallyNumber}
          description={`${getPipeTypeLabel(tally.pipeType)} · ${getGradeLabel(tally.grade)} · ${
            tally.outerDiameter
          }" OD`}
          titleAdornment={
            <div className="flex gap-2">
              <Badge variant={tally.status === 'IN_STOCK' ? 'default' : 'secondary'}>
                {tally.status.replace(/_/g, ' ')}
              </Badge>
              <Badge variant={condition.variant}>{condition.label}</Badge>
            </div>
          }
        />
      </motion.div>

      <motion.div variants={fadeUp}>
        <DetailLayout
          rail={
            <>
              <RailCard
                title="Specification"
                icon={<Ruler className="h-4 w-4 text-muted-foreground" />}
              >
                <DetailList
                  items={[
                    { label: 'Type', value: getPipeTypeLabel(tally.pipeType) },
                    { label: 'Grade', value: getGradeLabel(tally.grade) },
                    { label: 'Outer diameter', value: `${tally.outerDiameter}"` },
                    { label: 'Wall thickness', value: `${tally.wallThickness}"` },
                    { label: 'Weight', value: `${tally.weight} lb/ft` },
                    { label: 'Connection', value: tally.connection, when: !!tally.connection },
                    { label: 'Range', value: tally.range, when: !!tally.range },
                  ]}
                />
              </RailCard>

              <RailCard
                title="Provenance"
                icon={<Factory className="h-4 w-4 text-muted-foreground" />}
              >
                <DetailList
                  items={[
                    { label: 'Heat number', value: tally.heatNumber, when: !!tally.heatNumber },
                    { label: 'Mill', value: tally.millName, when: !!tally.millName },
                    {
                      label: 'Certificate',
                      value: tally.certificateId ? (
                        <Link
                          to={`/certificates/${tally.certificateId}`}
                          className="underline-offset-4 hover:underline"
                        >
                          View certificate
                        </Link>
                      ) : (
                        'None linked'
                      ),
                      // Pipe with no mill certificate cannot be run on a
                      // regulated well, so its absence is worth flagging.
                      tone: tally.certificateId ? 'default' : 'warning',
                    },
                    {
                      label: 'Location',
                      value: (
                        <Link
                          to={`/locations/${tally.locationId}`}
                          className="underline-offset-4 hover:underline"
                        >
                          {tally.location?.name ?? tally.locationId}
                        </Link>
                      ),
                    },
                    {
                      label: 'Rack',
                      value: tally.storageLocation,
                      when: !!tally.storageLocation,
                    },
                    {
                      label: 'Last tallied',
                      value: tally.lastTalliedAt ? formatDate(tally.lastTalliedAt) : 'Never',
                      tone: tally.lastTalliedAt ? 'default' : 'warning',
                    },
                    {
                      label: 'Tallied by',
                      value: tally.talliedBy?.fullName,
                      when: !!tally.talliedBy,
                    },
                  ]}
                />
              </RailCard>
            </>
          }
          main={
            <div className="space-y-6">
              <Card>
                <CardHeader>
                  <h3 className="text-base font-semibold text-foreground">Inventory</h3>
                  <CardDescription>
                    What this tally holds, as counted at the last tally
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <StatStrip
                    stats={[
                      {
                        // Usable leads. It is the number somebody calls pipe out
                        // against, and the one that decides whether a string can
                        // be run — "on the rack" is inventory, not availability.
                        label: 'Usable',
                        value: (
                          <span
                            className={shortfall > 0 ? 'text-status-warning-foreground' : undefined}
                          >
                            {formatNumber(usableFt)} ft
                          </span>
                        ),
                      },
                      { label: 'On the rack', value: `${formatNumber(totalFt)} ft` },
                      {
                        label: 'Joints',
                        value: `${formatNumber(f?.joints ?? tally.joints)}`,
                      },
                    ]}
                  />

                  {shortfall > 0 && (
                    <p className="border-t pt-3 text-sm text-status-warning-foreground">
                      {formatNumber(shortfall)} ft on the rack cannot go into a string specified at{' '}
                      {condition.label}
                      {f && f.downgradedJoints > 0 && (
                        <>
                          {' '}
                          — {f.downgradedJoints} joint{f.downgradedJoints === 1 ? '' : 's'}{' '}
                          inspected below the ordered class
                        </>
                      )}
                      {f && f.scrapJoints > 0 && (
                        <>
                          {f.downgradedJoints > 0 ? ', and ' : ' — '}
                          {f.scrapJoints} scrapped
                        </>
                      )}
                      .
                    </p>
                  )}

                  {f && (
                    <p className="text-xs text-muted-foreground">
                      Average joint {f.avgJointLength.toFixed(2)} ft.
                    </p>
                  )}
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <h3 className="text-base font-semibold text-foreground">Joints</h3>
                  <CardDescription>
                    {joints.length
                      ? `${joints.length} joints recorded individually`
                      : 'No joints recorded individually — the tally carries a count only'}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <DataTable<YardPipeJoint>
                    pagination
                    data={joints}
                    columns={jointColumns}
                    emptyTitle="No joint detail"
                    emptyDescription="This tally records a count and footage, but not individual joints."
                  />
                </CardContent>
              </Card>

              {tally.notes && (
                <Card>
                  <CardHeader>
                    <h3 className="text-base font-semibold text-foreground">Notes</h3>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-muted-foreground">{tally.notes}</p>
                  </CardContent>
                </Card>
              )}
            </div>
          }
        />
      </motion.div>
    </motion.main>
  );
}
